import { defineStore } from 'pinia';
import { ref } from 'vue';
import jsonFetch from '@globals/jsonFetch';
import { HelloResponse } from '@globals/types/Hello';
import { getPermissionStatus } from '@customer/utilities/Permissions';

export interface TutorialVideo {
	title: string;
	name: string;
	link?: string;
	linkLabel?: string;
	wasRated?: boolean;
}

export interface TutorialVideoTrigger {
	body: string;
	header: string;
	videos: TutorialVideo[];
}

export interface TutorialModal {
	name: string;
	regexp: RegExp;
	important: boolean;
	displayed: boolean;
	skipped: boolean;
}

export const useCustomerOnboardingStore = defineStore('customerOnboarding', () => {
	const state = {
		/**
		 * Szczegóły profilu użytkownika, które są wykorzystywane do wyświetlania ekranów onboardingowych.
		 * Byłoby do wywalenia, ale coś na profilu korzysta jeszcze ze sprawdzenia profilu.
		 */
		profileDetails: ref({
			hasDescription: false,
		}),

		/**
		 * Czy konto użytkownika wymaga weryfikacji w danej sesji?
		 */
		needsVerification: ref(''),

		/**
		 * Czy konto użytkownika wymaga onboardingu w danej sesji?
		 */
		canShowOnboarding: ref(false),

		/**
		 * Czy pominąć początkowy ekran przy weryfikacji?
		 */
		skipVerificationIntro: ref(false),

		tutorialVideos: ref<Record<string, TutorialVideo>>({}),

		/**
		 * Dane aktualnie oglądanego wideotutoriala
		 */
		globalTutorialVideo: ref<{
			source: string;
			videos: TutorialVideo[];
		}>({
			source: '',
			videos: [],
		}),

		/**
		 * Lista automatycznie pojawiających się modali
		 */
		tutorialModals: ref<TutorialModal[]>([]),

		/**
		 * Data ostatniego wyświetlenia modala z kolejki
		 */
		lastModalDate: ref(0),

		/**
		 * Lista nieprzeczytanych tutoriali
		 */
		tutorials: ref<string[]>([]),

		/**
		 * Lista kroków do uzupełnienia profilu
		 */
		onboardingTodo: ref<string[]>([]),

		/**
		 * Czy darmowe odkrycia za uzupełnienie profilu zostały odebrane?
		 */
		onboardingFreeUncoversClaimed: ref(false),

		/**
		 * Czy pobraliśmy kroki do uzupełnienia profilu?
		 */
		onboardingIsLoaded: ref(false),

		/**
		 * Adres do regulaminu onboardingu
		 */
		onboardingTosLink: ref(''),

		/**
		 * Procent uzupełnienia profilu
		 */
		onboardingPercentageValue: ref(0),

		/**
		 * Uzupełnione kroki onboardingu
		 */
		onboardingCompletedSteps: ref<string[]>([]),

		/**
		 * Czy dane z krokami onboardingu zostały załadowane?
		 */
		onboardingDataLoaded: ref(false),
	};

	const actions = {
		/**
		 * Uaktualnia dane dotyczące ekranów onboardingowych z początkowej odpowiedzi serwera.
		 */
		update(rsvp: HelloResponse) {
			// Wyświetl informację o konieczności weryfikacji Autenti zamiast onboardingu.
			if (rsvp.flags.includes('needsVerification')) {
				state.needsVerification.value = 'autenti';
				actions.showModal('autentiVerification');
			}

			// Pobierz wideotutoriale.
			if (rsvp.user?.videos) {
				state.tutorialVideos.value = rsvp.user.videos;
			}

			// Pobierz informacje o nieprzeczytanych tutorialach.
			if (rsvp.user?.tutorials) {
				state.tutorials.value = rsvp.user.tutorials;
			}

			// Pobierz kroki onboardingu.
			if (rsvp.flags.includes('canShowOnboarding')) {
				state.canShowOnboarding.value = true;
			}

			state.profileDetails.value = {
				hasDescription: rsvp.user?.hasDescription || false,
			};
		},

		/**
		 * Usuń tutorial z listy nieprzeczytanych.
		*/
		removeTutorial(name: string) {
			const idx = state.tutorials.value.findIndex((i: string) => i === name);
			if (idx >= 0) { state.tutorials.value.splice(idx, 1); }
		},

		/**
		 * Uaktualnia stan wypełnienia profilu.
		 */
		async updateOnboardingSteps() {
			try {
				const rsvp = await jsonFetch<{
					todo: string[];
					onboardingFreeUncoversClaimed: boolean;
					tosLink: string;
				}>('/myoferteo/konto-json/onboarding-completion-info.do');

				state.onboardingTodo.value = rsvp.todo || [];
				state.onboardingFreeUncoversClaimed.value = rsvp.onboardingFreeUncoversClaimed;
				state.onboardingIsLoaded.value = true;
				state.onboardingTosLink.value = rsvp.tosLink || '/public/docs/regulamin_3_darmowe_odkrycia.pdf';
			} catch (err) {
				console.warn('updateOnboardingSteps', err);
			}
		},

		/**
		 * Sprawdza, czy na danym URL-u można wyświetlić ekran onboardingowy zamiast zawartości strony.
		 */
		canShowOnboarding(href: string) {
			if (href.includes('/moje-zapytania') || href.includes('/formularz-kontaktowy')) { return ''; }
			if (href.includes('goToOnboarding=true')) { return 'onboarding'; }
			if (href.includes('goToAutenti=true')) { state.needsVerification.value = 'autenti'; }
			if (state.needsVerification.value && href.includes('/konto') && !href.includes('/zapytanie')) { return 'autenti'; }

			const result = href.includes('/zapytania') || href.includes('/kontakty') || href.endsWith('/konto');
			return (result && state.canShowOnboarding.value) ? 'onboarding' : '';
		},

		/**
		 * Wysyła informacje analityczne o onboardingu
		 */
		async onboardingAnalytics(eventName: string, step = '', eventParams: Record<string, string> = {}) {
			window.dataLayer?.push({
				event: 'ga_event',
				event_name: eventName.replace('$', step),
				event_params: eventParams,
			});

			let dbEventName = '';
			let dbEventParams = {};
			const menuOptionStep = {
				Inf: 'general',
				Des: 'description',
				Pho: 'photo',
				Ver: 'verify',
			} as Record<string, string>;

			switch (eventName) {
				case 'seen_start_onboardingNew':
					dbEventName = 'DISPLAY';
					dbEventParams = { step: 'start' };
					break;
				case 'click_start_onboardingNew':
					dbEventName = 'BTN_ACTION_CLICK';
					dbEventParams = { step: 'start' };
					break;
				case 'click_back$_onboardingNew':
					dbEventName = 'BTN_BACK_CLICK';
					dbEventParams = { step: step.toLowerCase() };
					break;
				case 'click_$_onboardingNew':
					dbEventName = 'BTN_ACTION_CLICK';
					dbEventParams = { step: step.toLowerCase() };
					break;
				case 'seen_$_onboardingNew':
					dbEventName = 'DISPLAY';
					dbEventParams = { step: step.toLowerCase() };
					break;
				case 'seen_completed_onboardingNew':
					dbEventName = 'DISPLAY';
					dbEventParams = {
						step: 'completed',
						percentage: state.onboardingPercentageValue.value,
					};
					break;
				case 'click_exit_onboardingNew':
					dbEventName = 'CLOSE';
					dbEventParams = {
						step: 'completed',
					};
					break;
				case 'click_checkRfp_onboardingNew':
					dbEventName = 'CLICK';
					dbEventParams = {
						button: 'checkRfp',
					};
					break;
				case 'click_showCard_onboardingNew':
					dbEventName = 'CLICK';
					dbEventParams = {
						button: 'showCard',
					};
					break;
				case 'click_menu$_onboardingNew':
					dbEventName = 'CHOSE_MENU_OPTION';
					dbEventParams = {
						step: menuOptionStep[step],
					};
					break;
			}

			if (dbEventName) {
				await jsonFetch('/user/note-user-action-by-name.do', {
					method: 'POST',
					body: {
						actionType: dbEventName,
						actionGroup: 'CUSTOMER_ONBOARDING',
						actionParameters: dbEventParams,
					},
				});
			}
		},

		/**
		 * Pobiera informacje o stopniu uzupełnienia profilu
		 */
		async getOnboardingSteps() {
			try {
				const rsvp = await jsonFetch<{
					percentageValue: number;
					completedSteps: ('basicInfo' | 'description' | 'gallery' | 'phone')[];
				}>('/myoferteo/konto-json/onboarding-get-steps.do');
				if (Object.keys(rsvp).length) {
					state.onboardingPercentageValue.value = rsvp.percentageValue;
					state.onboardingCompletedSteps.value = rsvp.completedSteps;
					state.onboardingDataLoaded.value = true;
					return rsvp;
				}
			} catch (err) {
				console.warn(`getOnboardingSteps failed.`, err);
			}

			state.onboardingPercentageValue.value = 0;

			return {
				completedSteps: [],
				percentageValue: 0,
			};
		},

		/**
		 * Pobiera i ustawia listę modali, które mogą być wyświetlone w trakcie korzystania z serwisu.
		 */
		async getModalList() {
			let rsvp;
			try {
				rsvp = await jsonFetch<{
					lastModalDate: number;
					modals: {
						name: string;
						regexp: string;
						important: boolean;
						onlyDesktop?: boolean;
						onlyMobile?: boolean;
						onlyWeb?: boolean;
						onlyApp?: boolean;
					}[];
				}>('/public/json/modal/get-modal-list.do');

				if (!state.lastModalDate.value) {
					state.lastModalDate.value = rsvp.lastModalDate || 0;
				}

				const isApp = typeof wrapee !== 'undefined';
				const isMobile = window.innerWidth < 640;

				rsvp.modals = rsvp.modals.filter((m) => {
					if (m.onlyWeb && isApp) { return false; }
					if (m.onlyApp && !isApp) { return false; }
					if (m.onlyDesktop && isMobile) { return false; }
					if (m.onlyMobile && !isMobile) { return false; }
					return true;
				});

				if (!rsvp.modals) { return; }

				// { name: 'notificationsUpdate', regexp: '^((?!konto\/zapytanie).)*$', important: false },

				for (const modal of rsvp.modals) {
					let skipped = false;
					// Specjalny przypadek dla powiadomień Push - są one zależne od urządzenia.
					if (modal.name === 'pushNotifModal') {
						const permissionStatus = await getPermissionStatus('Permission.notification');
						let permissionDefault;
						if (isApp) {
							// Dla aplikacji mobilnej sprawdzamy wszystkie statusy, oprócz 'granted'.
							permissionDefault = permissionStatus !== 'granted';
						} else {
							// Dla web sprawdzamy tylko status 'default'.
							permissionDefault = permissionStatus === 'default';
						}
						if (!permissionDefault || !isMobile) { skipped = true; }
					}

					// Dodaj do listy modali, które mogą być wyświetlone.
					state.tutorialModals.value.push({
						name: modal.name,
						regexp: new RegExp(modal.regexp),
						important: modal.important,
						displayed: false,
						skipped,
					});
				}
			} catch (err) {
				console.warn('getModalList', { err });
			}
		},

		/**
		 * Sprawdza czy można wyświetlić okno dialogowe
		 * @param pathname URL strony na której ma się wyświetlić okno
		 * @param modals Lista nazw okien
		 * @returns Nazwa okna do wyświetlenia, jeżeli można jakieś wyświetlić.
		 */
		canShowModal(modals: string[], pathname = '') {
			let timeoutValue = 15 * 60 * 1000; // 15 minut
			const customTimeoutValue = Number(window.localStorage.getItem('canShowModalTimeout'));
			if (customTimeoutValue) { timeoutValue = customTimeoutValue * 1000; }

			const tutorialModals = state.tutorialModals.value;
			const pathnameToCheck = pathname || window.location.pathname;
			const wasAnyDisplayed = state.lastModalDate.value + timeoutValue > Number(new Date()); // Czy jakikolwiek inny był wyświetlony w ciągu ostatniego czasu?

			// Sprawdź wszystkie modale na ścieżce. Jeżeli nie ma żadnych, to wyjdź.
			const modalsOnPathAndNotDisplayed = tutorialModals.filter((m) => m.regexp.test(pathnameToCheck) && !m.displayed && !m.skipped);
			if (!modalsOnPathAndNotDisplayed.length) { return ''; }

			// Gdy jeszcze nie został wyświetlony żaden modal i jest w podanych w parametrach, to wyświetl pierwszy z listy.
			if (!wasAnyDisplayed) {
				const firstModalName = modalsOnPathAndNotDisplayed[0].name;
				return modals.includes(firstModalName) ? firstModalName : '';
			}

			// W przeciwnym przypadku, sprawdź, czy któryś z modali jest ważny.
			const importantModalsOnPath = modalsOnPathAndNotDisplayed.filter((m) => m.important);
			if (!importantModalsOnPath.length) { return ''; }
			const firstImportantModalName = modalsOnPathAndNotDisplayed[0].name;
			return modals.includes(firstImportantModalName) ? firstImportantModalName : '';
		},

		/**
		 * Zapisuje informację o tym, że jakiś modal został wyświetlony podczas trwania tej sesji
		 * oraz ustawia datę ostatniego wyświetlenia.
		 */
		showModal(modalName: string, skipServerRequest = false) {
			const modal = state.tutorialModals.value.find((m) => m.name === modalName);
			if (modal) {
				modal.displayed = true;
			}

			const date = Number(new Date());
			state.lastModalDate.value = date;

			if (skipServerRequest) { return; }
			jsonFetch('/myoferteo/konto-json/modal/show-modal.do', {
				method: 'POST',
				body: { modalName },
			});
		},

		/**
		 * Ustawia flagę, czy dany modal może być pominięty, np. jeżeli zaktualizowaliśmy jakieś dane w sesji.
		 */
		skipModal(modalName: string, value: boolean) {
			const modal = state.tutorialModals.value.find((m) => m.name === modalName);
			if (modal) {
				modal.skipped = value;
			}
		},
	};

	const getters = {};

	return { ...state, ...actions, ...getters };
});
